import React, { useEffect, useState, useRef } from 'react';
import CustomTaskDot from './CustomTaskDot';
import './CustomTaskDot.css'
import moment from 'moment';
const WaveChart = ({ data, color, fillColor, width, height }) => {
    const maxValue = Math.max(...data.map(item => item.value));
    const minValue = Math.min(...data.map(item => item.value));
    const range = maxValue - minValue || 1; // Prevent division by zero
    let position = 0
    // Calculate points for the wave path
    const getPathPoints = () => {
        const points = [];
        const step = width / (data.length - 1);

        data.forEach((item, index) => {
            const x = index * step;
            // Normalize the value to fit within the chart height
            const y = height - ((item.value - minValue) / range) * height;
            points.push({ x, y, ...item });
        });

        return points;
    };

    const points = getPathPoints();

    // Group all tasks by color across all points
    // const getAllTasksByColor = () => {
    //     const colorGroups = {};

    //     points.forEach(point => {
    //         if (!point.tasks || point.tasks.length === 0) return;

    //         point.tasks.forEach(task => {
    //             const endDate = new Date(task.endDate || task.selectDate);
    //             const today = new Date();
    //             const daysDiff = Math.floor((endDate - today) / (1000 * 60 * 60 * 24));

    //             let taskColor;
    //             if (task.taskStatus === 'done') {
    //                 taskColor = daysDiff >= 0 ? '#4CAF50' : '#F44336';
    //             } else {
    //                 if (daysDiff < 0) {
    //                     taskColor = '#F44336';
    //                 } else if (daysDiff <= 3) {
    //                     taskColor = '#FF9800';
    //                 } else {
    //                     taskColor = '#2196F3';
    //                 }
    //             }

    //             if (!colorGroups[taskColor]) {
    //                 colorGroups[taskColor] = {
    //                     tasks: [],
    //                     status: getStatusLabel(task.taskStatus, daysDiff),
    //                     color: taskColor,
    //                     // Use the first point's position for this color group
    //                     x: point.x,
    //                     y: point.y
    //                 };
    //             }
    //             colorGroups[taskColor].tasks.push(task);
    //         });
    //     });

    //     return Object.values(colorGroups);
    // };

    const getAllTasksByColor = () => {
        const colorGroups = {};

        points.forEach(point => {
            if (!point.tasks || point.tasks.length === 0) return;

            point.tasks.forEach(task => {
                const endDate = new Date(task.endDate || task.selectDate);
                const today = new Date();
                const daysDiff = Math.floor((endDate - today) / (1000 * 60 * 60 * 24));

                let taskColor;
                if (task.taskStatus === 'done') {
                    taskColor = daysDiff >= 0 ? '#4CAF50' : '#F44336';
                } else {
                    if (daysDiff < 0) {
                        taskColor = '#F44336';
                    } else if (daysDiff <= 3) {
                        taskColor = '#FF9800';
                    } else {
                        taskColor = '#2196F3';
                    }
                }

                if (!colorGroups[taskColor]) {
                    colorGroups[taskColor] = {
                        tasks: [],
                        status: getStatusLabel(task.taskStatus, daysDiff),
                        color: taskColor,
                        positions: [],
                        // Add analysis properties
                        minX: Infinity,
                        maxX: -Infinity,
                        avgX: 0,
                        count: 0
                    };
                }

                // Add task to group
                colorGroups[taskColor].tasks.push(task);

                // Track position data for analysis
                colorGroups[taskColor].positions.push({ x: point.x, y: point.y });
                colorGroups[taskColor].minX = Math.min(colorGroups[taskColor].minX, point.x);
                colorGroups[taskColor].maxX = Math.max(colorGroups[taskColor].maxX, point.x);
                colorGroups[taskColor].count++;
                colorGroups[taskColor].avgX = ((colorGroups[taskColor].avgX * (colorGroups[taskColor].count - 1)) + point.x) / colorGroups[taskColor].count;
            });
        });

        // Now analyze the spatial distribution of each color group
        Object.values(colorGroups).forEach(group => {
            // Calculate cluster information
            group.clusterInfo = {
                spread: group.maxX - group.minX,  // How wide the group is spread
                density: group.count / (group.maxX - group.minX || 1),  // Dots per pixel
                isClustered: (group.maxX - group.minX) < (width * 0.2)  // Arbitrary threshold for clustering
            };
        });

        return Object.values(colorGroups);
    };

    const getStatusLabel = (taskStatus, daysDiff) => {
        if (taskStatus === 'done') {
            return daysDiff >= 0 ? 'Completed On Time' : 'Completed Late';
        } else {
            if (daysDiff < 0) return 'Overdue';
            if (daysDiff <= 3) return 'Due Soon (≤3 days)';
            return 'On Track';
        }
    };

    // Create SVG path for the wave
    const createWavePath = () => {
        if (points.length === 0) return '';
        let path = `M ${points[0].x} ${points[0].y}`;


        for (let i = 1; i < points.length; i++) {
            const prevPoint = points[i - 1];
            const currentPoint = points[i];

            // Create a smooth curve between points
            const controlX1 = prevPoint.x + (currentPoint.x - prevPoint.x) * 0.3;
            const controlY1 = prevPoint.y;
            const controlX2 = currentPoint.x - (currentPoint.x - prevPoint.x) * 0.3;
            const controlY2 = currentPoint.y;

            path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${currentPoint.x} ${currentPoint.y}`;
        }

        // Close the path to fill the area
        path += ` L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

        return path;
    };

    const colorGroups = getAllTasksByColor();





    const [showTooltip, setShowTooltip] = useState(false);
    const [hoveredTaskStatus, setHoverTaskStatus] = useState('');
    const [hoveredTasks, setHoveredTasks] = useState([]);

    const tooltipRef = useRef(null);

    // Add effect to handle clicks outside the tooltip
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setShowTooltip(false);
            }
        };

        if (showTooltip) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showTooltip]);
    const [positions, setPositions] = useState({
        x:0,
        y:0
    });

    const handlePositions = () => {
        const coords = colorGroups.flatMap(group =>
            group.positions.map(pos => ({ x: pos.x, y: pos.y }))
        );
        setPositions(coords[0]);
    };
    useEffect(()=>{
        handlePositions()
    },[])

   console.log(positions)
    return (
        <>
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                {/* Wave fill */}
                <path
                    d={createWavePath()}
                    fill={fillColor}
                    stroke="none"
                />

                {/* Wave line */}
                <path
                    d={createWavePath().split(' L')[0]}
                    stroke={color}
                    strokeWidth={2}
                    fill="none"
                />

                {/* Render one dot per color group */}
                {colorGroups.map((group, groupIndex) => {
                    return group.positions.map((position, posIndex) => (
                        <CustomTaskDot
                            key={`${group.color}-${groupIndex}-${posIndex}`}
                            cx={position.x}
                            cy={position.y}
                            color={group.color}
                            status={group.status}
                            tasks={group.tasks}
                            showTooltip={showTooltip}
                            setShowTooltip={setShowTooltip}
                            setHoverTaskStatus={setHoverTaskStatus}
                            setHoveredTasks={setHoveredTasks}
                            // You could optionally add cluster-based attributes here
                            opacity={group.clusterInfo.isClustered ? 0.8 : 1}
                        />
                    ));
                })}
            </svg>
            {
                showTooltip && (
                    <div
                        ref={tooltipRef}  // Add ref to the tooltip
                        onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing
                        className='custom_tooltip_wrapper' style={{
                            position: 'absolute',
                            top: `-${positions.y + 90}px`,
                            left:`${positions.x + 20 }px`
                        }}>
                        <p><strong>Status:</strong> {hoveredTaskStatus}</p>
                        <p><strong>Tasks {hoveredTasks?.length}:</strong></p>
                        <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                            {hoveredTasks?.length > 0 ? hoveredTasks?.map((task, i) => (
                                <li key={i} style={{ marginBottom: '5px' }}>
                                    {i + 1}) {task.taskName} (Due: {moment(task.endDate || task.selectDate).format('MMM D, YYYY')})
                                </li>
                            )) : (
                                <li style={{ marginBottom: '5px' }}>
                                    No Tasks Data Found ...
                                </li>
                            )}
                        </ul>
                    </div>
                )
            }
        </>


    );
};

export default WaveChart;